export type MenuWidth = 'auto' | number | 'anchorEl';

export const MENU_STYLE = {
  PADDING_Y: 1,
  MIN_WIDTH: 100,
  MAX_WIDTH: 400,
} as const;

export const MENU_ITEM_STYLE = {
  PADDING_X: 2,
  PADDING_Y: 1,
  COLUMN_GAP: 2,
  ICON_COLUMN_WIDTH: 20,
  MIN_HEIGHT: 36,
} as const;

/** How many items fit in a menu before the list scrolls. */
export const MENU_MAX_VISIBLE_ITEMS = 12;

/**
 * Max height (px) for a scrollable menu list that shows
 * {@link MENU_MAX_VISIBLE_ITEMS} rows, including list vertical padding.
 * Uses the theme spacing unit (Nodon default: 4).
 */
export const getMenuListMaxHeightPx = (spacingPx = 4): number =>
  MENU_STYLE.PADDING_Y * spacingPx * 2 +
  MENU_ITEM_STYLE.MIN_HEIGHT * MENU_MAX_VISIBLE_ITEMS;

export const SUBMENU_TIMEOUTS = {
  OPEN: 200,
  CLOSE: 200,
} as const;

export type MenuFuzzySearchOptions = {
  threshold?: number;
  minMatchCharLength?: number;
};

/** Side a submenu opens towards, relative to its parent item. */
export type SubmenuSide = 'left' | 'right';

export type MenuParentContext = {
  registerItemVisibility: (itemIndex: number, isVisible: boolean) => void;
  registerChildIndex: (index: number) => void;
  unregisterChildIndex: (index: number) => void;
};

export type MenuContextValue = {
  isOpen: boolean;
  closeMenu: () => void;
  closeAllMenus: () => void;
  searchString: string;
  setSearchString: (value: string) => void;
  matchesSearch: (text: string) => boolean;
  focusedIndex: number | null;
  setFocusedIndex: (index: number | null) => void;
  registerItem: (ref: HTMLElement | null, index?: number) => number;
  unregisterItem: (index: number) => void;
  registerItemVisibility: (itemIndex: number, isVisible: boolean) => void;
  registerChildIndex: (index: number) => void;
  unregisterChildIndex: (index: number) => void;
  itemVisibility: Map<number, boolean>;
  registerSubmenu: (
    itemIndex: number,
    openSubmenu: () => void,
    closeSubmenu: () => void,
  ) => void;
  unregisterSubmenu: (itemIndex: number) => void;
  closeOtherSubmenus: (exceptIndex: number | null) => void;
  itemRefs: Map<number, HTMLElement>;
  fuzzySearchOptions: MenuFuzzySearchOptions;
  width: MenuWidth;
  /** Side that submenus of this menu should open towards. */
  submenuSide: SubmenuSide;
};
